import { useState, type ReactNode } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
  EyeIcon,
  HistoryIcon,
  Loader2Icon,
  PencilIcon,
} from 'lucide-react';
import type { GeneratedDocumentVersionDTO } from '@shared/schemas/document-generation';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useChatExperience } from '../chat-experience';
import { useChatToolInteraction } from '../chat-tool-interaction';
import { buildCodegenChatDocument, buildGenerationCanvasDocument } from '../codegen-document';
import { getChatErrorMessage } from '../chat-errors';
import { downloadRemoteFile } from '../download-remote-file';
import { useGeneratedDocumentVersions } from '../hooks/useDocumentGeneration';
import { getDocumentGeneration } from '../services/document-generation.api';

const PAGE_SIZE = 10;

type VersionActionProps = {
  children: ReactNode;
  disabled: boolean;
  label: string;
  onClick: () => void;
};

/** Action d'une ligne de version : bouton icône discret, libellé en infobulle et pour les lecteurs d'écran. */
function VersionAction({ children, disabled, label, onClick }: VersionActionProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          aria-label={label}
          className="text-muted-foreground hover:text-foreground"
          disabled={disabled}
          onClick={onClick}
          size="icon-sm"
          type="button"
          variant="ghost"
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}

function formatVersionDate(value: string) {
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

const VERSION_STATUS_LABELS: Record<GeneratedDocumentVersionDTO['status'], string> = {
  dead: 'Abandonnée',
  failed: 'Échec',
  generating: 'En cours',
  pending: 'En attente',
  succeeded: 'Terminée',
};

async function downloadVersion(input: {
  conversationId: string;
  generationId: string;
}) {
  const status = await getDocumentGeneration(input.conversationId, input.generationId);
  if (status.status !== 'succeeded') {
    throw new Error('document_version_unavailable');
  }
  await downloadRemoteFile(status.result.downloadUrl, status.result.filename);
}

type ChatDocumentVersionsMenuProps = {
  conversationId: string;
  /** Version actuellement affichée (repère visuel dans la liste). */
  currentGenerationId?: string;
  generatedDocumentId: string;
  /** Classe de l'icône du déclencheur (ex. icône plus grande dans la carte de génération). */
  iconClassName?: string;
  /** Classe du libellé du bouton (ex. masqué sous une largeur donnée). */
  labelClassName?: string;
  /**
   * Ouvre une version du moteur blocks dans le canvas déjà affiché. Sans ce rappel,
   * la version est ouverte dans le panneau comme un nouveau document.
   */
  onOpenBlocksVersion?: (version: { generationId: string; revisionNumber: number }) => void;
  title: string;
  triggerClassName?: string;
  triggerVariant?: 'ghost' | 'outline';
};

export function ChatDocumentVersionsMenu({
  conversationId,
  currentGenerationId,
  generatedDocumentId,
  iconClassName,
  labelClassName,
  onOpenBlocksVersion,
  title,
  triggerClassName,
  triggerVariant = 'outline',
}: ChatDocumentVersionsMenuProps) {
  const { openDocument } = useChatExperience();
  const { beginDocumentRevision } = useChatToolInteraction();
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pendingGenerationId, setPendingGenerationId] = useState<string | null>(null);
  const versionsQuery = useGeneratedDocumentVersions({
    conversationId,
    documentId: generatedDocumentId,
    enabled: open,
    page,
    pageSize: PAGE_SIZE,
  });
  async function handleOpenVersion(version: GeneratedDocumentVersionDTO) {
    const { generationId } = version;
    if (version.engine === 'blocks') {
      // Le canvas se construit à partir de la génération : aucun fichier à résoudre.
      if (onOpenBlocksVersion) {
        onOpenBlocksVersion({ generationId, revisionNumber: version.revisionNumber });
      } else {
        openDocument(buildGenerationCanvasDocument({ generationId, kind: version.kind, title }));
      }
      setOpen(false);
      return;
    }
    setPendingGenerationId(generationId);
    try {
      const status = await getDocumentGeneration(conversationId, generationId);
      if (status.status !== 'succeeded') {
        throw new Error('document_version_unavailable');
      }
      openDocument(buildCodegenChatDocument({ result: status.result, title }));
      setOpen(false);
    } catch (error) {
      toast.error(getChatErrorMessage(error));
    } finally {
      setPendingGenerationId(null);
    }
  }

  async function handleDownloadVersion(generationId: string) {
    setPendingGenerationId(generationId);
    try {
      await downloadVersion({ conversationId, generationId });
    } catch (error) {
      toast.error(getChatErrorMessage(error));
    } finally {
      setPendingGenerationId(null);
    }
  }

  return (
    <Popover
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
          setPage(1);
        }
      }}
      open={open}
    >
      <PopoverTrigger asChild>
        <Button
          aria-label={`Afficher les versions de ${title}`}
          className={cn('shrink-0', triggerClassName)}
          size="sm"
          type="button"
          variant={triggerVariant}
        >
          <HistoryIcon className={cn('size-3.5', iconClassName)} data-icon="inline-start" />
          <span className={labelClassName}>Versions</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[22rem] p-3">
        <div className="mb-3">
          <p className="text-sm font-semibold">Historique des versions</p>
          <p className="text-xs text-muted-foreground">
            {versionsQuery.data?.items.some((version) => version.engine === 'blocks')
              ? 'Vous pouvez modifier n’importe quelle version terminée.'
              : 'Seule la dernière version réussie peut être modifiée.'}
          </p>
        </div>

        {versionsQuery.isPending ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" aria-hidden />
            Chargement des versions
          </div>
        ) : versionsQuery.isError ? (
          <div className="py-4 text-sm text-destructive">
            {getChatErrorMessage(versionsQuery.error)}
          </div>
        ) : versionsQuery.data?.items.length === 0 ? (
          <div className="py-4 text-sm text-muted-foreground">Aucune version disponible.</div>
        ) : (
          <ul className="-mx-1 flex max-h-80 flex-col overflow-y-auto">
            {versionsQuery.data?.items.map((version) => {
              const isLatestSucceeded = version.generationId === versionsQuery.data?.latestSucceededGenerationId;
              const isPending = pendingGenerationId === version.generationId;
              const isSucceeded = version.status === 'succeeded';
              const isShown = version.generationId === currentGenerationId;
              const canEdit = isSucceeded && (isLatestSucceeded || version.engine === 'blocks');
              const actionsDisabled = !isSucceeded || Boolean(pendingGenerationId);
              const label = `la version ${version.revisionNumber}`;
              return (
                <li
                  className={cn(
                    'group/version flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-muted/60',
                    isShown && 'bg-muted/60',
                  )}
                  key={version.generationId}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium">Version {version.revisionNumber}</p>
                      {isShown ? <Badge className="h-[1.125rem] px-1.5 text-[11px]" variant="secondary">Affichée</Badge> : null}
                      {isSucceeded ? null : (
                        <Badge className="h-[1.125rem] px-1.5 text-[11px]" variant="outline">
                          {VERSION_STATUS_LABELS[version.status]}
                        </Badge>
                      )}
                    </div>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground" title={version.revisionInstruction ?? undefined}>
                      {formatVersionDate(version.createdAt)}
                      {version.revisionInstruction ? <> · <span className="text-foreground/75">{version.revisionInstruction}</span></> : null}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-0.5">
                    {isPending ? (
                      <Loader2Icon aria-label="Chargement" className="mx-1.5 size-4 animate-spin text-muted-foreground" />
                    ) : (
                      <>
                        <VersionAction
                          disabled={actionsDisabled}
                          label={`Ouvrir ${label}`}
                          onClick={() => void handleOpenVersion(version)}
                        >
                          <EyeIcon aria-hidden />
                        </VersionAction>
                        <VersionAction
                          disabled={actionsDisabled}
                          label={`Télécharger ${label}`}
                          onClick={() => void handleDownloadVersion(version.generationId)}
                        >
                          <DownloadIcon aria-hidden />
                        </VersionAction>
                        {canEdit ? (
                          <VersionAction
                            disabled={Boolean(pendingGenerationId)}
                            label={`Modifier ${label}`}
                            onClick={() => {
                              beginDocumentRevision({
                                baseGenerationId: version.generationId,
                                generatedDocumentId,
                                title,
                                version: version.revisionNumber,
                              });
                              setOpen(false);
                            }}
                          >
                            <PencilIcon aria-hidden />
                          </VersionAction>
                        ) : null}
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {(versionsQuery.data?.meta.pageCount ?? 0) > 1 ? (
          <div className="mt-3 flex items-center justify-between border-t border-border/60 pt-2">
            <Button
              aria-label="Page précédente"
              disabled={page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              size="icon-xs"
              type="button"
              variant="ghost"
            >
              <ChevronLeftIcon aria-hidden />
            </Button>
            <span className="text-xs text-muted-foreground">
              Page {page} sur {versionsQuery.data?.meta.pageCount}
            </span>
            <Button
              aria-label="Page suivante"
              disabled={page >= (versionsQuery.data?.meta.pageCount ?? 1)}
              onClick={() => setPage((current) => current + 1)}
              size="icon-xs"
              type="button"
              variant="ghost"
            >
              <ChevronRightIcon aria-hidden />
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
